<template>
  <block-item-card
    ref="loansMaturity"
    :title="$t('EVE.Section.SharpestEVEChanges.Blocks.Largest6MonthMove.Title')"
    class="fill-height d-flex flex-column"
  >
    <template #actions>
      <v-spacer />
      <div class="d-flex align-center ml-4">
        <div class="d-flex align-center mr-4">
          <div
            class="mr-2"
            :style="`width: 12px; height: 12px; background: ${$vuetify.theme.themes.light['synthesis-ui-blue-04']}; border-radius: 100%;`"
          />
          <span class="text-size-12 synthesis-ui-grey-01--text font-weight-semibold">{{ fromDateFmt }}</span>
        </div>
        <div class="d-flex align-center">
          <div
            class="mr-2"
            :style="`width: 12px; height: 12px; background: ${$vuetify.theme.themes.light['synthesis-ui-green-02']}; border-radius: 100%;`"
          />
          <span class="text-size-12 synthesis-ui-grey-01--text font-weight-semibold">{{ toDateFmt }}</span>
        </div>
      </div>
    </template>

    <v-lazy
      :options="{threshold: .5}"
      transition="fade-transition"
    >
      <e-chart
        :autoresize="true"
        :option="chartOpts"
        :style="`height: 400px;`"
        class="chart"
      />
    </v-lazy>
    <div class="eve-change-item">
      <span class="eve-change-item-label">
        <v-icon
          color="black"
          size="10"
          class="mr-2"
          style="margin-top: -2px;"
        >
          mdi-circle
        </v-icon>
        {{ eveChangeItemLabel }}:&nbsp;
      </span>
      <span
        class="eve-change-item-value"
        :class="{'eve-change-item-value-negative': largestPvChange < 0}"
      >
        {{ largestPvChangeFormatted }}
      </span>
    </div>
  </block-item-card>
</template>
    <script>
import BlockItemCard      from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin   from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use }                 from 'echarts/core'
import { BarChart }            from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import { CanvasRenderer }      from 'echarts/renderers'

import { GridComponent, LegendComponent, TitleComponent, TooltipComponent, TransformComponent } from 'echarts/components'
import { formatDateShort, toNumberFormatNotationShort } from '@/lib/utils/helper'

use([
  BarChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
  CanvasRenderer,
  UniversalTransition
])

export default {
  name      : 'LargestSixMonthMove',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Object,
      default: () => null
    }
  },
  data () {
    return {}
  },
  computed: {
    currencySymbol () {
      return this.data?.baseCurrency?.displaySymbol || null
    },
    sharpestEveChanges () {
      return this.data?.result?.sharpestEveChanges || null
    },
    sixMonthsSharpestEveChangeItem () {
      return this.sharpestEveChanges?.sixMonthsSharpestEveChangeItem || null
    },
    fromDate () {
      return this.sixMonthsSharpestEveChangeItem?.fromDate || null
    },
    fromDateFmt () {
      return formatDateShort(this.fromDate, false, 0)
    },
    toDate () {
      return this.sixMonthsSharpestEveChangeItem?.toDate || null
    },
    toDateFmt () {
      return formatDateShort(this.toDate, false, 0)
    },
    fromRates () {
      return this.sixMonthsSharpestEveChangeItem?.fromRates || null
    },
    toRates () {
      return this.sixMonthsSharpestEveChangeItem?.toRates || null
    },
    maturities () {
      return this.sixMonthsSharpestEveChangeItem?.maturities || null
    },
    largestPvChange () {
      return this.sixMonthsSharpestEveChangeItem?.largestPvChange || null
    },
    chartOpts () {
      if (!this.sixMonthsSharpestEveChangeItem) {
        return {}
      }

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null
            }

            return `
                        <div style="margin: 4px 0;">
                            <span style="display: inline-block; width: 12px; height: 12px; background: ${params[1].color}; margin-right: 8px;"></span>
                            <span>${params[1].axisValue}: ${params[1].value}%</span>
                            <br/>
                            <span style="display: inline-block; width: 12px; height: 12px; background: ${params[0].color}; margin-right: 8px;"></span>
                            <span>${params[0].axisValue}: ${params[0].value}%</span>
                            </div>
                        </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 30,
          top         : 20,
          right       : 10,
          bottom      : 10,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.maturities.map(item => item.toLowerCase()),
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          axisLabel: {
            margin       : -15,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value) => {
              return value + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [
          {
            name     : 'From Rates',
            type     : 'line',
            data     : this.fromRates,
            itemStyle: {
              color: this.themeColorRGBA('synthesis-ui-blue-04')
            },
            lineStyle: {
              width: 3
            },
            symbol    : 'emptyCircle',
            symbolSize: 10,
            smooth    : true
          },
          {
            name     : 'To Rates',
            type     : 'line',
            data     : this.toRates,
            itemStyle: {
              color: this.themeColorRGBA('synthesis-ui-green-02')
            },
            lineStyle: {
              width: 3
            },
            symbol    : 'emptyCircle',
            symbolSize: 10,
            smooth    : true
          },
          {
            name     : 'band-base',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData.lower,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible
            areaStyle: null,
            z        : 1,
            silent   : true,
            tooltip  : { show: false }
          },
          {
            name     : 'band-blue',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData.diffBlue,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible line, we only want the area
            areaStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  { offset: 0, color: this.themeColorRGBA('synthesis-ui-blue-08', 0.8) },
                  { offset: 1, color: this.themeColorRGBA('synthesis-ui-green-08', 0.8) }
                ]
              }
            },
            z      : 1,
            silent : true,
            tooltip: { show: false }
          },
          {
            name     : 'band-orange',
            type     : 'line',
            stack    : 'band',
            data     : this.bandData.diffGreen,
            symbol   : 'none',
            lineStyle: { width: 0 }, // invisible line, area only
            areaStyle: {
              color: {
                type      : 'linear',
                x         : 0,
                y         : 0,
                x2        : 0,
                y2        : 1,
                colorStops: [
                  // use same subtle tint so the band looks unified
                  { offset: 0, color: this.themeColorRGBA('synthesis-ui-blue-08', 0.8) },
                  { offset: 1, color: this.themeColorRGBA('synthesis-ui-green-08', 0.8) }
                ]
              }
            },
            z      : 1,
            silent : true,
            tooltip: { show: false }
          }

        ]
      }
    },
    bandData () {
      const a = this.fromRates || []
      const b = this.toRates || []
      const n = Math.min(a.length, b.length)
      const lower = new Array(n)
      const diffBlue = new Array(n) // blue above orange
      const diffGreen = new Array(n) // green above blue

      for (let i = 0; i < n; i++) {
        const A = Number(a[i] ?? 0)
        const B = Number(b[i] ?? 0)
        if (A >= B) {
          lower[i] = B
          diffBlue[i] = A - B
          diffGreen[i] = 0
        } else {
          lower[i] = A
          diffBlue[i] = 0
          diffGreen[i] = B - A
        }
      }
      return { lower, diffBlue, diffGreen }
    },
    eveChangeItemLabel () {
      return this.sixMonthsSharpestEveChangeItem?.largestPvChange > 0 ? this.$t('EVE.Section.SharpestEVEChanges.Blocks.PositiveChange') : this.$t('EVE.Section.SharpestEVEChanges.Blocks.NegativeChange')
    },
    largestPvChangeFormatted () {
      if (!this.largestPvChange) {
        return '-'
      }

      const prefix =   (this.largestPvChange > 0 ? '' : '') + this.currencySymbol

      return prefix + toNumberFormatNotationShort(Math.abs(this.largestPvChange))
    }
  },
  created () {}
}
  </script>
  <style scoped>
    .chart {
      width: 100%;
    }
    .eve-change-item {
      padding: 15px 20px;
      background-color: var(--v-synthesis-ui-grey-06-base);
      border-radius: 4px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin:20px ;
    }

    .eve-change-item-label {
      font-weight: 500;
    }

    .eve-change-item-value {
      padding: 4px 12px;
      background-color: var(--v-synthesis-ui-green-07-base);
      border-radius: 4px;
      font-size: 12px;
      font-weight: 700;
      color: var(--v-synthesis-ui-green-01-base);
    }

    .eve-change-item-value-negative {
      background-color: var(--v-synthesis-ui-red-07-base);
      color: var(--v-synthesis-ui-red-01-base);
    }
  </style>
